package fyi.moko.hotpl.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color

// HOTPL brand: "Warm Journal" (docs/design-ref/.../warm_journal/DESIGN.md) — a cream
// paper base with an amber/moss/terracotta accent trio, replacing the earlier coral-red
// scheme. Light values are the mockups' literal hex tokens (DESIGN.md is canonical
// where a mockup disagrees with it — see the design-branch plan). Dark values are
// derived from the same source's primary-fixed/secondary-fixed/tertiary-fixed tones,
// which M3's tonal-palette convention defines specifically for building a matching
// dark scheme, rather than hand-picked independently.
private val LightColors = lightColorScheme(
    primary = Color(0xFF8D4B00),
    onPrimary = Color(0xFFFFFFFF),
    primaryContainer = Color(0xFFB15F00),
    onPrimaryContainer = Color(0xFFFFFBFF),
    secondary = Color(0xFF4E644A),
    onSecondary = Color(0xFFFFFFFF),
    secondaryContainer = Color(0xFFD0EAC8),
    onSecondaryContainer = Color(0xFF546A50),
    tertiary = Color(0xFF944433),
    onTertiary = Color(0xFFFFFFFF),
    tertiaryContainer = Color(0xFFB35C49),
    onTertiaryContainer = Color(0xFFFFFBFF),
    background = Color(0xFFFBF9F5),
    onBackground = Color(0xFF1B1C1A),
    surface = Color(0xFFFBF9F5),
    onSurface = Color(0xFF1B1C1A),
    surfaceVariant = Color(0xFFE4E2DE),
    onSurfaceVariant = Color(0xFF554336),
    outline = Color(0xFF887364),
    outlineVariant = Color(0xFFDBC2B0),
    surfaceContainerLowest = Color(0xFFFFFFFF),
    surfaceContainerLow = Color(0xFFF5F3EF),
    surfaceContainer = Color(0xFFEFEEEA),
    surfaceContainerHigh = Color(0xFFEAE8E4),
    surfaceContainerHighest = Color(0xFFE4E2DE),
    surfaceBright = Color(0xFFFBF9F5),
    surfaceDim = Color(0xFFDBDAD6),
    inverseSurface = Color(0xFF30312E),
    inverseOnSurface = Color(0xFFF2F0ED),
    inversePrimary = Color(0xFFFFB77D),
    surfaceTint = Color(0xFF904D00),
)
private val DarkColors = darkColorScheme(
    primary = Color(0xFFFFB77D),
    onPrimary = Color(0xFF2F1500),
    primaryContainer = Color(0xFF6E3900),
    onPrimaryContainer = Color(0xFFFFDCC3),
    secondary = Color(0xFFB5CDAE),
    onSecondary = Color(0xFF0C200B),
    secondaryContainer = Color(0xFF374C34),
    onSecondaryContainer = Color(0xFFD0EAC8),
    tertiary = Color(0xFFFFB4A4),
    onTertiary = Color(0xFF3E0500),
    tertiaryContainer = Color(0xFF793020),
    onTertiaryContainer = Color(0xFFFFDAD3),
    background = Color(0xFF16140F),
    onBackground = Color(0xFFECE8E0),
    surface = Color(0xFF16140F),
    onSurface = Color(0xFFECE8E0),
    surfaceVariant = Color(0xFF4A4238),
    onSurfaceVariant = Color(0xFFD8C9BA),
    outline = Color(0xFFA08D76),
    outlineVariant = Color(0xFF4F4738),
    surfaceContainerLowest = Color(0xFF100E0A),
    surfaceContainerLow = Color(0xFF211E17),
    surfaceContainer = Color(0xFF262219),
    surfaceContainerHigh = Color(0xFF302C22),
    surfaceContainerHighest = Color(0xFF3B362A),
    surfaceBright = Color(0xFF3D382C),
    surfaceDim = Color(0xFF16140F),
    inverseSurface = Color(0xFFECE8E0),
    inverseOnSurface = Color(0xFF302C22),
    inversePrimary = Color(0xFF8D4B00),
    surfaceTint = Color(0xFFFFB77D),
)

/** App-wide Material 3 theme for HOTPL — "Warm Journal" palette + Literata/Be Vietnam Pro type. */
@Composable
fun HotplTheme(
    darkTheme: Boolean = isSystemInDarkTheme(),
    content: @Composable () -> Unit,
) {
    MaterialTheme(
        colorScheme = if (darkTheme) DarkColors else LightColors,
        typography = warmJournalTypography(),
        content = content,
    )
}
